(() => {
  'use strict';

  const SDK_URL = 'https://assets.findation.com/javascripts/w-adv-13.min.js';
  const API_KEY = '195e9d300fb9a2af0548ee72e4f799da513c42d5919e70bf2c97899d2d7e';
  const BUTTON_IFRAME_ID = 'findation-button-iframe';
  const LOADING_MESSAGE = 'Loading shade matcher';

  let sdkPromise = null;
  let activeWidget = null;

  function loadSdk() {
    if (window.Findation && typeof window.Findation.init === 'function') {
      return Promise.resolve(window.Findation);
    }

    if (sdkPromise) return sdkPromise;

    sdkPromise = new Promise((resolve, reject) => {
      let script = document.querySelector(
        'script[data-findation-sdk], ' +
          'script[src^="https://assets.findation.com/javascripts/w-adv-13.min.js"]'
      );
      const existingScript = Boolean(script);

      if (!script) {
        script = document.createElement('script');
        script.src = SDK_URL;
        script.async = true;
        script.setAttribute('data-findation-sdk', '');
      }

      let settled = false;
      let timeoutId;

      const cleanup = () => {
        window.clearTimeout(timeoutId);
        script.removeEventListener('load', onLoad);
        script.removeEventListener('error', onError);
      };

      const fail = (error) => {
        if (settled) return;
        settled = true;
        cleanup();
        script.remove();
        sdkPromise = null;
        reject(error);
      };

      const onLoad = () => {
        if (!window.Findation || typeof window.Findation.init !== 'function') {
          fail(new Error('Findation SDK loaded without init API'));
          return;
        }

        if (settled) return;
        settled = true;
        cleanup();
        script.dataset.findationLoaded = 'true';
        resolve(window.Findation);
      };

      const onError = () => fail(new Error('Unable to load Findation SDK'));

      script.addEventListener('load', onLoad, {once: true});
      script.addEventListener('error', onError, {once: true});

      timeoutId = window.setTimeout(() => {
        fail(new Error('Findation SDK timed out'));
      }, 10000);

      if (!existingScript) {
        document.head.appendChild(script);
      } else if (script.dataset.findationLoaded === 'true') {
        Promise.resolve().then(onLoad);
      }
    });

    return sdkPromise;
  }

  class FindationOnDemand extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;

      this._trigger = this.querySelector('[data-findation-trigger]');
      this._mount = this.querySelector('[data-findation-mount]');
      this._status = this.querySelector('[data-findation-status]');

      if (!this._trigger || !this._mount || !this.dataset.findationProductId) {
        return;
      }

      this._bound = true;
      this._handleClick = this.open.bind(this);
      this._trigger.addEventListener('click', this._handleClick);
    }

    disconnectedCallback() {
      this._trigger?.removeEventListener('click', this._handleClick);
      this._bound = false;

      if (activeWidget === this) {
        window.Findation?.closeWidget?.();
        this.reset();
        activeWidget = null;
      }
    }

    setBusy(busy) {
      this._trigger.disabled = busy;
      this._trigger.toggleAttribute('aria-busy', busy);

      if (!this._status) return;

      if (busy) {
        this._status.textContent = LOADING_MESSAGE;
      } else if (this._status.textContent === LOADING_MESSAGE) {
        this._status.textContent = '';
      }
    }

    async open() {
      if (this._opening) return;

      this._opening = true;
      this.setBusy(true);

      try {
        await this.ensureReady();

        if (!this.isConnected) return;

        if (typeof window.Findation?.openWidget !== 'function') {
          throw new Error('Findation open API is unavailable');
        }

        this.setBusy(false);
        window.Findation.openWidget();
      } catch (error) {
        this.setBusy(false);

        if (this.isConnected && this._status) {
          this._status.textContent = 'Shade matcher is unavailable. Please try again.';
        }

        console.warn('[Findation]', error);
      } finally {
        if (this.isConnected) this.setBusy(false);
        this._opening = false;
      }
    }

    ensureReady() {
      if (this._ready && activeWidget === this) {
        return Promise.resolve();
      }

      if (this._initPromise) return this._initPromise;

      const initialization = (async () => {
        const Findation = await loadSdk();

        if (!this.isConnected) {
          throw new Error('Findation element was removed');
        }

        if (activeWidget && activeWidget !== this) {
          Findation.closeWidget?.();
          activeWidget.reset();
        }

        document.getElementById(BUTTON_IFRAME_ID)?.remove();
        activeWidget = this;

        await new Promise((resolve, reject) => {
          let settled = false;
          let timeoutId;

          const finish = (error) => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timeoutId);

            if (error) {
              reject(error);
            } else {
              resolve();
            }
          };

          Findation.widgetEmbedded = (iframe) => {
            const remoteButton =
              iframe && iframe.nodeType === Node.ELEMENT_NODE
                ? iframe
                : document.getElementById(BUTTON_IFRAME_ID);

            if (settled) {
              remoteButton?.remove();
              return;
            }

            if (!this.isConnected) {
              remoteButton?.remove();
              finish(new Error('Findation element was removed'));
              return;
            }

            if (!remoteButton) {
              finish(new Error('Findation button iframe is unavailable'));
              return;
            }

            remoteButton.hidden = true;
            remoteButton.tabIndex = -1;
            remoteButton.setAttribute('aria-hidden', 'true');

            this._remoteButton = remoteButton;
            this._ready = true;
            finish();
          };

          timeoutId = window.setTimeout(() => {
            finish(new Error('Findation button timed out'));
          }, 10000);

          try {
            Findation.init(this._mount, API_KEY, {
              productID: this.dataset.findationProductId,
              buttonHeight: '40px',
              buttonWidth: '100%',
            });
          } catch (error) {
            finish(error);
          }
        });
      })();

      this._initPromise = initialization.catch((error) => {
        this.reset();

        if (activeWidget === this) {
          activeWidget = null;
        }

        throw error;
      });

      return this._initPromise;
    }

    reset() {
      this._remoteButton?.remove();
      this.querySelector(`#${BUTTON_IFRAME_ID}`)?.remove();

      this._remoteButton = null;
      this._ready = false;
      this._initPromise = null;
    }
  }

  if (!customElements.get('findation-on-demand')) {
    customElements.define('findation-on-demand', FindationOnDemand);
  }
})();
